import React, { useEffect, useState } from 'react';
import type { DashboardModel } from '../../hooks/useDashboard';

/** "Mohamed Ayaaz Suleman" → "M. Suleman" */
const shortPilot = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0][0]}. ${parts[parts.length - 1]}`;
};

/** Tick-based clock for map last-updated age */
const useMapAge = (latestUpdate: string | null | undefined) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 15000);
    return () => clearInterval(id);
  }, []);

  if (!latestUpdate) return null;
  const dt = new Date(latestUpdate);
  if (Number.isNaN(dt.getTime())) return null;
  const diffSec = Math.floor((Date.now() - dt.getTime()) / 1000);
  if (diffSec < 10) return 'Just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ago`;
};

const DashboardMapHud: React.FC<DashboardModel> = ({
  dark,
  liveAircrafts,
  selectedAircraft,
  detailTrayMode,
  activeTab,
  aircraftFlightInfo,
  weatherData,
  mapRef,
  isValidCoordinate,
  setSelectedAircraft,
  setIsFullscreen,
}) => {
  // Latest GPS timestamp across all aircraft — used for map freshness indicator
  const latestGpsUpdate = liveAircrafts.reduce<string | null>((best, ac) => {
    const ts = ac.telemetry?.lastUpdate;
    if (!ts) return best;
    if (!best) return ts;
    return new Date(ts) > new Date(best) ? ts : best;
  }, null);
  const mapAge = useMapAge(latestGpsUpdate);

  return (
    <>
      {/* Bottom aircraft pills */}
      {detailTrayMode !== 'full' && (
        <div className="absolute bottom-7 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
          {liveAircrafts.filter(ac => ac.telemetry && isValidCoordinate(ac.telemetry.lat, ac.telemetry.lng)).map(ac => {
            const speed = ac.telemetry?.groundSpeed || 0;
            const isFlying = speed > 30;
            const shortTail = ac.tailNumber.replace('5H-', '');
            const dest = aircraftFlightInfo[ac.tailNumber]?.destination;
            const lastLoc = aircraftFlightInfo[ac.tailNumber]?.lastLocation;
            const pilot = aircraftFlightInfo[ac.tailNumber]?.pilotName;
            const isSelected = selectedAircraft?.id === ac.id;
            const routeText = dest ? `${shortTail} → ${dest}` : lastLoc ? `${shortTail} · ${lastLoc}` : shortTail;

            return (
              <button
                key={ac.id}
                onClick={() => {
                  setSelectedAircraft(ac);
                  if (ac.telemetry && mapRef.current) {
                    try { mapRef.current.flyTo({ center: [ac.telemetry.lng, ac.telemetry.lat], zoom: 12, duration: 1200 }); } catch {}
                  }
                }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border backdrop-blur-xl transition-all ${
                  isSelected
                    ? 'bg-blue-600/90 border-blue-400/60 shadow-lg shadow-blue-500/30'
                    : dark ? 'bg-slate-900/85 border-white/15 hover:border-white/30' : 'bg-white/95 border-slate-200 hover:border-slate-300 shadow-sm'
                }`}
              >
                {/* Status dot */}
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isFlying ? 'bg-emerald-400 animate-pulse' : 'bg-blue-400'}`} />
                <span className={`text-[10px] font-bold ${dark ? 'text-white' : 'text-slate-800'}`}>{routeText}</span>
                <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full ${
                  isFlying
                    ? 'bg-emerald-500/20 text-emerald-600'
                    : isSelected
                    ? 'text-blue-200'
                    : dark ? 'text-slate-400' : 'text-slate-500'
                }`}>
                  {isFlying ? 'In Flight' : 'Arrived'}
                </span>
                {pilot && (
                  <span className={`text-[8px] truncate max-w-[72px] ${dark ? 'text-amber-300/80' : 'text-slate-500'}`}>
                    · {shortPilot(pilot)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Right-side controls */}
      <div className="absolute top-6 right-6 flex flex-col gap-2 z-20">
        {/* Fullscreen toggle — shows exit arrows when fullscreen */}
        <button
          onClick={() => setIsFullscreen(v => !v)}
          title="Toggle fullscreen"
          className={`w-12 h-12 backdrop-blur-xl rounded-xl flex items-center justify-center shadow-xl transition-all hover:scale-110 ${
            dark
              ? 'bg-gradient-to-br from-slate-900/95 to-slate-800/95 border border-white/20 text-white/80 hover:text-white hover:border-indigo-400/50'
              : 'bg-white border border-slate-200 text-slate-600 hover:text-indigo-600 hover:border-indigo-300'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/>
          </svg>
        </button>

        {mapRef.current && (
          <>
            <button
              onClick={() => mapRef.current?.zoomIn({ duration: 300 })}
              className={`w-12 h-12 backdrop-blur-xl rounded-xl flex items-center justify-center shadow-xl transition-all hover:scale-110 ${
                dark
                  ? 'bg-gradient-to-br from-slate-900/95 to-slate-800/95 border border-white/20 text-white/80 hover:text-white hover:border-indigo-400/50'
                  : 'bg-white border border-slate-200 text-slate-600 hover:text-indigo-600 hover:border-indigo-300'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6"/></svg>
            </button>
            <button
              onClick={() => mapRef.current?.zoomOut({ duration: 300 })}
              className={`w-12 h-12 backdrop-blur-xl rounded-xl flex items-center justify-center shadow-xl transition-all hover:scale-110 ${
                dark
                  ? 'bg-gradient-to-br from-slate-900/95 to-slate-800/95 border border-white/20 text-white/80 hover:text-white hover:border-indigo-400/50'
                  : 'bg-white border border-slate-200 text-slate-600 hover:text-indigo-600 hover:border-indigo-300'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4"/></svg>
            </button>
            <button
              onClick={() => {
                if (selectedAircraft?.telemetry) {
                  mapRef.current?.flyTo({ center: [selectedAircraft.telemetry.lng, selectedAircraft.telemetry.lat], zoom: 15, duration: 1500 });
                } else {
                  mapRef.current?.flyTo({ center: [36.633, -3.367], zoom: 13, duration: 1500 });
                }
              }}
              className="w-12 h-12 bg-gradient-to-br from-indigo-600/95 to-indigo-700/95 backdrop-blur-xl border border-indigo-400/50 rounded-xl flex items-center justify-center text-white shadow-2xl transition-all hover:scale-110"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/></svg>
            </button>
          </>
        )}
      </div>

      {/* Top-left: weather strip */}
      <div className={`absolute top-6 left-6 z-20 flex items-center gap-2 backdrop-blur-xl px-3 py-1.5 rounded-lg border ${dark ? 'bg-slate-900/80 border-white/10' : 'bg-white/95 border-slate-200 shadow-sm'}`}>
        <span className={`text-[9px] font-bold ${dark ? 'text-slate-400' : 'text-slate-500'}`}>HTKA</span>
        {weatherData ? (
          <>
            {weatherData.temp && <span className={`text-[10px] font-bold font-mono ${dark ? 'text-white' : 'text-slate-800'}`}>{weatherData.temp}</span>}
            {weatherData.conditions && <span className={`text-[9px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{weatherData.conditions}</span>}
            {weatherData.wind && <span className="text-[9px] text-slate-500">{weatherData.wind}</span>}
          </>
        ) : (
          <span className={`text-[9px] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>Loading…</span>
        )}
      </div>

      {/* Bottom-left: map last-updated badge (Gmail-style) */}
      {mapAge && (
        <div className={`absolute bottom-7 left-6 z-20 flex items-center gap-1.5 backdrop-blur-md px-2.5 py-1 rounded-lg border ${dark ? 'bg-slate-900/70 border-white/8 text-slate-400' : 'bg-white/80 border-slate-200/70 text-slate-400 shadow-sm'}`}>
          <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
          <span className="text-[8px] font-semibold tracking-wide">Updated {mapAge}</span>
        </div>
      )}

      {/* Selected aircraft mini card (top-right, below controls) */}
      {detailTrayMode !== 'full' && activeTab !== 'history' && selectedAircraft?.telemetry && (() => {
        const tel = selectedAircraft.telemetry;
        const info = aircraftFlightInfo[selectedAircraft.tailNumber];
        const isFlying = (tel.groundSpeed || 0) > 30;
        const shortTail = selectedAircraft.tailNumber.replace('5H-', '');
        return (
          <div className={`absolute top-6 right-20 z-20 w-56 backdrop-blur-xl rounded-xl border shadow-xl overflow-hidden ${dark ? 'bg-slate-900/92 border-white/15' : 'bg-white border-slate-200'}`}>
            <div className={`px-4 py-2.5 border-b ${isFlying ? 'bg-blue-600/80 border-white/10' : dark ? 'bg-slate-700/80 border-white/10' : 'bg-slate-100 border-slate-200'}`}>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${isFlying ? 'bg-emerald-400 animate-pulse' : 'bg-blue-400'}`}></div>
                <span className={`text-[11px] font-bold tracking-wide ${dark ? 'text-white' : 'text-slate-800'}`}>
                  {info?.destination ? `${shortTail} → ${info.destination}` : shortTail}
                </span>
                <span className={`ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full ${isFlying ? 'bg-emerald-500/20 text-emerald-400' : dark ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-500'}`}>
                  {isFlying ? 'In Flight' : 'Arrived'}
                </span>
              </div>
            </div>

            <div className={`px-4 py-3 space-y-2.5 text-[11px] ${dark ? '' : 'text-slate-700'}`}>
              {info?.pilotName && (
                <div className="flex items-center justify-between">
                  <span className={dark ? 'text-slate-400' : 'text-slate-500'} style={{ fontWeight: 500 }}>Pilot</span>
                  <span className={`font-bold truncate max-w-[120px] ${dark ? 'text-amber-300' : 'text-indigo-600'}`}>{info.pilotName}</span>
                </div>
              )}
              {info?.lastLocation && (
                <div className="flex items-center justify-between">
                  <span className={dark ? 'text-slate-400' : 'text-slate-500'} style={{ fontWeight: 500 }}>Location</span>
                  <span className={`font-bold truncate max-w-[120px] ${dark ? 'text-cyan-300' : 'text-cyan-600'}`}>{info.lastLocation}</span>
                </div>
              )}
              {tel.groundSpeed > 0 && (
                <div className="flex items-center justify-between">
                  <span className={dark ? 'text-slate-400' : 'text-slate-500'} style={{ fontWeight: 500 }}>Speed</span>
                  <span className={`font-mono font-bold ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>{Math.round(tel.groundSpeed)} <span className="text-[9px] text-slate-500">kt</span></span>
                </div>
              )}
              {tel.altitude > 0 && (
                <div className="flex items-center justify-between">
                  <span className={dark ? 'text-slate-400' : 'text-slate-500'} style={{ fontWeight: 500 }}>Altitude</span>
                  <span className={`font-mono font-bold ${dark ? 'text-blue-400' : 'text-blue-600'}`}>{Math.round(tel.altitude).toLocaleString()} <span className="text-[9px] text-slate-500">ft</span></span>
                </div>
              )}
              {tel.heading > 0 && (
                <div className="flex items-center justify-between">
                  <span className={dark ? 'text-slate-400' : 'text-slate-500'} style={{ fontWeight: 500 }}>Heading</span>
                  <span className={`font-mono font-bold ${dark ? 'text-indigo-400' : 'text-indigo-600'}`}>{Math.round(tel.heading)}°</span>
                </div>
              )}
              {tel.lastUpdate && (
                <div className={`flex items-center justify-between pt-1 border-t ${dark ? 'border-white/10' : 'border-slate-200'}`}>
                  <span className="text-slate-500 text-[9px]">Last Update</span>
                  <span className={`text-[9px] font-mono ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {new Date(tel.lastUpdate).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </>
  );
};

export default DashboardMapHud;
